import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import {
  type BorderSide,
  type RuntimeProps,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
} from "./shared";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { ImageChild } from "./imageChild";
import { MapChild, VideoChild } from "./mediaChildren";
import { type WBReveal } from "./reveal";

export function Image(props: RuntimeProps) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButton = (props.distTextButton as string | undefined) ?? "mb-2";
  const distContentImage =
    (props.distContentImage as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const buttonAlign =
    (props.buttonAlign as "left" | "center" | "right" | undefined) ?? "center";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 w-full flex flex-col",
          alignmentClass,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distContentImage,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames("flex flex-col", alignmentClass, leftWidth)}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                fontFamily={props.fontFamilyHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                fontFamily={props.fontFamilyHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <div className={distTextButton}>
              <Element
                is={TextChild}
                id="text"
                isVisible={props.isVisibleText as boolean | undefined}
                text={props.textText as string | undefined}
                fontSize={props.fontSizeText as string | undefined}
                fontFamily={props.fontFamilyText as string | undefined}
                textAlign={
                  props.textAlignText as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
              />
            </div>
            <div
              className={
                buttonAlign === "left"
                  ? "flex justify-start"
                  : buttonAlign === "center"
                    ? "flex justify-center"
                    : "flex justify-end"
              }
            >
              <Element is={Button} id="button" />
            </div>
          </div>
          <div className={rightWidth}>
            <Element is={ImageChild} id="image" />
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}

export function Map(props: RuntimeProps) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButton = (props.distTextButton as string | undefined) ?? "mb-2";
  const distContentMap =
    (props.distContentMap as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const buttonAlign =
    (props.buttonAlign as "left" | "center" | "right" | undefined) ?? "center";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 w-full flex flex-col",
          alignmentClass,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distContentMap,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames("flex flex-col justify-center", leftWidth)}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                fontFamily={props.fontFamilyHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                fontFamily={props.fontFamilyHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <div className={distTextButton}>
              <Element
                is={TextChild}
                id="text"
                isVisible={props.isVisibleText as boolean | undefined}
                text={props.textText as string | undefined}
                fontSize={props.fontSizeText as string | undefined}
                fontFamily={props.fontFamilyText as string | undefined}
                textAlign={
                  props.textAlignText as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
              />
            </div>
            <div
              className={
                buttonAlign === "left"
                  ? "flex justify-start"
                  : buttonAlign === "center"
                    ? "flex justify-center"
                    : "flex justify-end"
              }
            >
              <Element is={Button} id="button" />
            </div>
          </div>
          <div className={rightWidth}>
            <Element
              id="map"
              is={MapChild}
              isVisible={Boolean(props.isVisibleMap ?? true)}
              location={props.mapLocation as string | undefined}
              zoom={props.mapZoom as number | undefined}
              aspectRatio={props.aspectRatio as string | undefined}
              rounded={props.mapRounded as string | undefined}
              mapType={props.mapType as "m" | "k" | "h" | "p" | undefined}
            />
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}

export function Video(props: RuntimeProps) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButton = (props.distTextButton as string | undefined) ?? "mb-2";
  const distContentVideo =
    (props.distContentVideo as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const buttonAlign =
    (props.buttonAlign as "left" | "center" | "right" | undefined) ?? "center";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 w-full flex flex-col",
          alignmentClass,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distContentVideo,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames("flex flex-col", alignmentClass, leftWidth)}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                fontFamily={props.fontFamilyHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                fontFamily={props.fontFamilyHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <div className={distTextButton}>
              <Element
                is={TextChild}
                id="text"
                isVisible={props.isVisibleText as boolean | undefined}
                text={props.textText as string | undefined}
                fontSize={props.fontSizeText as string | undefined}
                fontFamily={props.fontFamilyText as string | undefined}
                textAlign={
                  props.textAlignText as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
              />
            </div>
            <div
              className={
                buttonAlign === "left"
                  ? "flex justify-start"
                  : buttonAlign === "center"
                    ? "flex justify-center"
                    : "flex justify-end"
              }
            >
              <Element is={Button} id="button" />
            </div>
          </div>
          <div className={rightWidth}>
            <Element
              id="video"
              is={VideoChild}
              isVisible={Boolean(props.isVisibleVideo ?? true)}
              url={props.videoUrl as string | undefined}
              aspectRatio={props.videoAspectRatio as string | undefined}
            />
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
